import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import TopBanner from '../../../components/topBanner/topBanner';
import imgBanner from '../../../images/banner/banner-tin-tuc.jpg';
import TitleCenter from '../../../components/title/TitleCenter';
import PageNotFound from '../../../components/notfound/PageNotFound';
import { normalizeLang } from '../../../utils/localeDataLoader';
import { getCustomPageBySlug } from '../../../apis/customPages';
import { normalizeUploadedImageUrl } from '../../../apis/uploadImage';
import './customPage.scss';

function CustomPageDetail() {
    const { t, i18n } = useTranslation();
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [pageData, setPageData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    // Lấy slug trực tiếp từ pathname hoặc params:
    // URL là /chinh-sach-bao-mat -> slug là "chinh-sach-bao-mat"
    // URL là /chinh-sach/chinh-sach-bao-mat -> slug là "chinh-sach/chinh-sach-bao-mat"
    // Hỗ trợ cả trường hợp có tiền tố cũ /trang/chinh-sach-bao-mat
    const rawPath = location.pathname.replace(/^\/+|\/+$/g, '');
    const cleanPath = rawPath.replace(/^(trang|page)\//, '');
    const slug = cleanPath || params.slug || rawPath;

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }, [slug]);

    useEffect(() => {
        let isMounted = true;
        const fetchPage = async () => {
            if (!slug || rawPath.startsWith('admin')) {
                setLoading(false);
                setError(true);
                return;
            }
            setLoading(true);
            setError(false);
            try {
                const languageCode = normalizeLang(i18n.language);
                const data = await getCustomPageBySlug(slug, languageCode);
                if (isMounted) {
                    if (data && data.isActive !== false) {
                        setPageData(data);
                    } else {
                        setError(true);
                    }
                }
            } catch (err) {
                console.error('Failed to load custom page:', err);
                if (isMounted) setError(true);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchPage();
        return () => { isMounted = false; };
    }, [slug, i18n.language]);

    if (!loading && (error || !pageData)) {
        return <PageNotFound />;
    }

    const pageTitle = pageData?.title || (loading ? 'Đang tải...' : 'Trang thông tin');
    const metaTitle = pageData?.metaTitle || `${pageTitle} | HSV Corp`;
    const metaDescription = pageData?.metaDescription || pageData?.summary || '';

    return (
        <div className="CustomPageDetail">
            <Helmet>
                <title>{metaTitle}</title>
                {metaDescription && <meta name="description" content={metaDescription} />}
                <meta property="og:title" content={metaTitle} />
                {metaDescription && <meta property="og:description" content={metaDescription} />}
                <meta property="og:type" content="article" />
            </Helmet>

            <TopBanner
                titleCenter="HSV CORP"
                imgBanner={imgBanner}
                pageName={i18n.language?.startsWith('en') ? 'Home' : 'Trang chủ'}
                text={pageTitle}
            />

            <div className="layout_container">
                {loading ? (
                    <div className="custom-page-container" style={{ textAlign: 'center', padding: '5rem 2rem' }}>
                        <div className="spinner-border text-success" role="status" style={{ width: '3rem', height: '3rem' }}>
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p style={{ marginTop: '1.5rem', color: '#64748b', fontSize: '1.6rem' }}>
                            Đang tải nội dung trang...
                        </p>
                    </div>
                ) : error || !pageData ? (
                    <div className="custom-page-container custom-page-empty">
                        <h3>Không tìm thấy trang</h3>
                        <p>Trang bạn đang tìm kiếm không tồn tại, đã bị gỡ bỏ hoặc tạm thời không khả dụng.</p>
                        <button
                            type="button"
                            className="btn btn-success"
                            style={{ backgroundColor: '#4DB748', borderColor: '#4DB748', fontSize: '1.5rem', padding: '0.8rem 2rem' }}
                            onClick={() => navigate('/')}
                        >
                            Quay về Trang chủ
                        </button>
                    </div>
                ) : (
                    <div className="custom-page-container">
                        <TitleCenter text={pageData.title} />

                        {pageData.summary && (
                            <div className="custom-page-summary">
                                {pageData.summary}
                            </div>
                        )}

                        {pageData.content && (
                            <div className="custom-page-body quill-content">
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: normalizeUploadedImageUrl(pageData.content)
                                    }}
                                />
                            </div>
                        )}

                        <div className="custom-page-footer-info">
                            <span>HSV Healthcare Corporation</span>
                            {pageData.updatedAt && (
                                <span>Cập nhật: {new Date(pageData.updatedAt).toLocaleDateString('vi-VN')}</span>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default CustomPageDetail;
